'use client';

import { Button, tw } from '@repo/ui';

/**
 * Props interface for the ClaimButton component
 */
interface ClaimButtonProps {
  /** The text to display inside the button */
  text: string;
  /** Optional click handler function */
  onClick?: () => void;
  /** Optional CSS classes to apply to the button */
  className?: string;
  /** Flag to show loading state */
  isLoading?: boolean;
  /** Flag to disable the button */
  disabled?: boolean;
  /** Flag to show a warning message */
  hasEnoughSol: boolean;
  /** Flag to show a warning message */
  hasSolWallet: boolean;
}

/**
 * A styled button component with glow effect for claim actions
 * The button features an animated background glow effect when enabled
 * and supports loading states and disabled functionality
 */
export const ClaimButton: React.FC<ClaimButtonProps> = ({
  text,
  onClick,
  className,
  isLoading = false,
  disabled = false,
  hasEnoughSol = true,
  hasSolWallet = true,
}) => {
  return (
    <div className='group relative inline-flex w-full hover:cursor-pointer'>
      {/* Animated glow effect background - only shown when button is enabled */}
      {!disabled ? (
        <div
          className={tw(
            'absolute -inset-px rounded-xl bg-[#05FFA9] opacity-40 blur-lg',
            'animate-tilt transition-all duration-1000',
            'group-hover:-inset-1 group-hover:opacity-70 group-hover:duration-200',
          )}
        ></div>
      ) : null}
      <div className='flex w-full flex-col gap-2'>
        {/* Main button component with theme and size presets */}
        <Button
          onClick={onClick}
          aria-label={`Button: ${text}`}
          extraClasses={tw('z-20', className)}
          isLoading={isLoading}
          disabled={disabled}
          fullWidth
          theme='claim'
          size='large'
        >
          {text}
        </Button>

        {!hasEnoughSol && hasSolWallet ? (
          <p className='text-xs text-[#FF6868]'>
            You need at least .01 SOL to continue.
          </p>
        ) : null}
      </div>
    </div>
  );
};
